import { computed, makeObservable } from 'mobx'
import { MeetingResultsSlide } from './MeetingResultsSlide'
import { sortQuestions } from '../../../sources-of-truth/sortQuestions'
import { StudentPeerReviewController } from '../../StudentAssignment/StudentPeerReviewController'

type EndOfSessionQuizPhase =
  | 'loading'
  | 'quiz'
  | 'peerReview'
  | 'waiting'
  | 'processing'
  | 'results'

export class EndOfSessionQuizSlide extends MeetingResultsSlide {
  private peerReviewController?: StudentPeerReviewController

  afterConstructor() {
    makeObservable(this, {
      questions: computed,
      phase: computed,
    })
  }

  private getPeerReviewController(): StudentPeerReviewController | undefined {
    const roomState = this.meeting.roomState
    if (
      !roomState.isLoaded ||
      !roomState.data.sectionId ||
      !roomState.data.assignmentId
    ) {
      return undefined
    }

    if (!this.peerReviewController) {
      this.peerReviewController = new StudentPeerReviewController(
        this.meeting.repository,
        {
          roomId: roomState.id,
          userId: this.meeting.repository.uid,
          sectionId: roomState.data.sectionId,
          assignmentId: roomState.data.assignmentId,
          peerReviewExcludesSelfReview:
            this.meeting.assignment.data.peerReviewExcludesSelfReview ?? false,
          userIdsInRoom: roomState.userIds,
        }
      )
      this.peerReviewController.initializeAsReviewer()
    }

    return this.peerReviewController
  }

  get questions() {
    // Filter by this slide's own stable id, not `currentSlide?.id`. On a Safari
    // refresh the LiveKit reconnect churns re-renders while `currentSlide` is
    // momentarily undefined; with the old filter `questions` went transiently
    // empty, and an empty list makes `phase`'s `every()` vacuously true — the
    // slide then advanced past the quiz to processing/results, blanking the
    // panel ("questions disappeared"). `this.id` is set once at construction
    // (see Slide base, which filters its own question helpers the same way), so
    // it does not flicker. When this slide is rendered it *is* the current
    // slide, so the two ids are otherwise identical.
    const endOfSessionQuestions = this.meeting.questions.filter(
      (q) => q.slideId === this.id && q.isQuiz
    )
    return sortQuestions({ questions: endOfSessionQuestions, slideIds: [] })
  }

  get titleIndex(): number {
    // loop over currentSlide.questions and find out if all are submitted
    for (const q of this.questions) {
      if (!q.isSubmitted) {
        return 0
      }
    }
    return 1
  }

  get canProceed() {
    return (
      this.meeting.endOfSessionQuizSlideCanProceed || this.meeting.hasFeedback
    )
  }

  get peerReviewControllerForView(): StudentPeerReviewController | undefined {
    return this.getPeerReviewController()
  }

  get hasQuizOrPeerReview(): boolean {
    return (
      this.questions.length > 0 ||
      this.peerReviewControllerForView?.peerReviewsEnabled.value === true
    )
  }

  get phase(): EndOfSessionQuizPhase {
    if (
      this.meeting.slideDeckQuestions.isLoading ||
      this.peerReviewControllerForView?.userYetToCompletePeerReview.isLoading ||
      // hasQuizOrPeerReview reads peerReviewsEnabled to pick the proceed
      // button's label; waiting for it here stops that label being rendered as
      // "Show Results" and then flipping to "Next Slide" once it resolves
      this.peerReviewControllerForView?.peerReviewsEnabled.isLoading
    )
      return 'loading'

    const allAnswered = this.questions.every(
      (question) => question.isAnswered && question.isSubmitted
    )

    if (this.questions.length > 0 && !allAnswered) return 'quiz'

    if (this.questions.length === 0 || allAnswered) {
      if (this.peerReviewControllerForView?.userYetToCompletePeerReview.value) {
        return 'peerReview'
      }

      // Hold finishers on the waiting screen (reconvened, but not progressed)
      // until the group reconvenes together. Only applies when there is a quiz
      // and someone to actually wait for. `presentUserIds` excludes the current
      // user, so `>= 1` means at least one OTHER present student (a real group
      // of 2+ present total); a lone student skips waiting entirely.
      //
      // Once everyone present (current user included) has finished there is
      // nobody left to wait for, so don't park the last finisher on the waiting
      // screen while the shared reconvene signal round-trips through Firestore —
      // that showed the screen for about a second before it auto-proceeded.
      // Presence is live, so a peer who refreshes or drops briefly takes the
      // count to zero and a finisher moves on rather than waiting for someone
      // the client can no longer see. That matches the lone-finisher rule and
      // is preferred to holding a student behind a peer who may never return;
      // the cost is that a transient disconnect in a 2-person group can skip
      // the waiting screen entirely. Revisit with a short presence grace
      // period if that proves disruptive in practice.
      const waitingApplies =
        this.questions.length > 0 &&
        this.meeting.presentUserIds.length >= 1 &&
        !this.meeting.endOfSessionAllPresentFinishedQuiz
      if (waitingApplies && !this.meeting.endOfSessionReconvened)
        return 'waiting'

      if (this.meeting.endOfSessionQuizSlideShouldProceed) return 'results'
      // if someone has proceeded, go to results, otherwise go to processing
      return 'processing'
    }

    return 'results'
  }
}
